<template>
  <div
    ref="root"
    data-qa="vacancy-form"
    :class="$style.root"
  >
    <component
      :is="vjsfFormComponent"
      v-if="vjsfFormComponent"
      :vacancy="vacancy"
      :child-id="childId"
      :request="vacancyRequest"
      :offers="offers"
      :submitting="submitting"
      :form-error="formError"
      :error-schema="errorSchema"
      @submit="handleVjsfSubmit"
      @cancel="$emit('cancel')"
      @dirty="handleDirty"
      @ready="$emit('ready')"
    />
  </div>
</template>

<i18n lang="json">
{
  "vacancy_missing_permission_error": {
    "ru_RU": "У&nbsp;вас нет прав создавать и&nbsp;управлять вакансиями. Обратитесь к&nbsp;управляющему рекрутеру",
    "en_US": "You do&nbsp;not have the rights to&nbsp;create and manage vacancies. Contact the managing recruiter"
  },
  "declined_taking_error": {
    "ru_RU": "Произошла ошибка: заявка уже была отклонена",
    "en_US": "An error occurred: Vacancy request has already been declined"
  },
  "missing_value_selected": {
    "ru_RU": "Выбранное значение удалено",
    "en_US": "A deleted value is selected"
  },
  "form.missing_value_selected": {
    "ru_RU": "Не удалось сохранить: было удалено выбранное значение из справочника. Выберите другое",
    "en_US": "Failed to save: the selected value was deleted from the list. Select another"
  }
}
</i18n>

<script>
import { filter, pick, omit, cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import VjsfChildVacancy from '@/modules/vacancy/vjsf-vacancy/child-vacancy.vue';
import VjsfMultiVacancy from '@/modules/vacancy/vjsf-vacancy/multivacancy.vue';
import VjsfRegularVacancy from '@/modules/vacancy/vjsf-vacancy/regular-vacancy.vue';
import { VacancyState } from '@/shared/legacy/types/vacancy-state';

export default {
  name: 'StartVacancy',
  provide() {
    return {
      // в самой форме данные по файлам - это массив id-шников
      // чтобы отрисовать файлы ссылками со всеми аттрибутами, нужно id отресолвить на объект файла
      $getAttachedFiles: this.getAttachedFiles
    };
  },
  props: {
    handleSubmit: {
      type: Function,
      required: true
    },
    position: {
      type: String,
      default: null
    },
    multiple: Boolean,
    reopen: Boolean,
    vacancy: {
      type: Object,
      required: true
    },
    vacancyRequest: {
      type: Object,
      default: null
    },
    offers: {
      type: Array,
      default: null
    },
    childId: {
      type: Number,
      default: null
    }
  },
  emits: ['cancel', 'touch', 'ready'],
  data() {
    return {
      fileInfos: this.vacancy.files ? [...this.vacancy.files] : [],
      submitting: false,
      submitError: null,
      lastSubmitErrorStatus: null
    };
  },
  computed: {
    isOnlyChildVacancyFieldsEditable() {
      if (this.multiple && this.vacancyRequest) {
        // В случае существующей мультивакансии, при создании дочерней вакансии
        // из заявки пользователь может только добавлять
        // дочернюю вакансию и редактирование общих полей отключено.
        // Для новой вакансии - показываем и родителя и дочку
        return Boolean(this.vacancy.id);
      }
      return Boolean(this.childId && this.vacancy?.blocks.some((b) => b.id === this.childId));
    },
    vjsfFormComponent() {
      // обычная вакансия
      if (!this.multiple) {
        return VjsfRegularVacancy;
      }
      // дочка (редактирование, создание из заявки)
      if (this.multiple && this.isOnlyChildVacancyFieldsEditable) {
        return VjsfChildVacancy;
      }
      if (this.multiple && !this.isOnlyChildVacancyFieldsEditable) {
        return VjsfMultiVacancy;
      }
      throw new Error('Unknown vacancy component');
    },
    // ошибки для отображения на полях формы
    errorSchema() {
      if (!this.submitError) {
        return {};
      }
      return Object.fromEntries(Object.entries(this.submitError).map(([key, value]) => {
        if (value === 'missing_value_selected') {
          return [key, [this.$i18n('missing_value_selected')]];
        }
        return [key, value];
      }));
    },
    // ошибки для отображения ко всей форме
    formError() {
      if (this.lastSubmitErrorStatus === 403) {
        return [this.$i18n('vacancy_missing_permission_error')];
      }
      if (!this.submitError) {
        return [];
      }
      if (Object.values(this.submitError).includes('missing_value_selected')) {
        // в этом случае не отображаем ошибку на форме
        return [];
        // return [this.$i18n('form.missing_value_selected')];
      }
      const errorKey = this.submitError.common?.[0] || 'error.unknown';
      if (errorKey === 'error.unique_fail') {
        return [this.$trlMessage('vacancy.not-unique.error')];
      }
      if (errorKey === 'Unknown vacancy request' || errorKey.startsWith('Vacancy requests not found')) {
        return [this.$i18n('declined_taking_error')];
      }
      // eslint-disable-next-line no-restricted-syntax
      return [this.$trlMessage(errorKey)];
    },
    ...mapGetters('vacancies', {
      getVacancyById: 'byId'
    })
  },
  watch: {
    errorSchema: {
      handler(errObj) {
        const keys = Object.keys(errObj);
        if (keys.length) {
          this.$nextTick(() => {
            this.$refs.root.querySelector('[name][aria-invalid="true"]')?.focus();
          });
        }
      },
      immediate: true
    }
  },
  methods: {
    handleDirty(isDirty) {
      if (isDirty) {
        this.$emit('touch');
      }
    },
    getAttachedFiles() {
      return this.fileInfos;
    },
    handleVjsfSubmit(data) {
      if (this.submitting) {
        return;
      }

      this.submitting = true;
      this.prepareForSubmit(data).then(({ form }) => {
        let payload = {
          ...this.vacancy,
          ...form,
          coworkers: data.recruiters
        };

        const omitCompany = (obj) => omit(obj, ['company']);
        payload = omitCompany(payload);
        payload.blocks = payload.blocks?.map(omitCompany);

        this.handleSubmit(payload)
          .then(() => {
            this.submitError = null;
            this.lastSubmitErrorStatus = null;
          })
          .catch((err) => {
            let errData = err.response?.body || null;
            // TODO: я был вынужден
            // проблемы две:
            // - конвертация формы вакансии на фронте (в JSONSchema. Т.е. если бэк отдаёт формат ошибок, который налезает на фронтоформу - это чудо)
            // - в принципе не стандартизированная работа с ошибками
            if (errData && typeof errData === 'object' && !Array.isArray(errData)) {
              errData = Object.fromEntries(Object.entries(errData).map(([key, value]) => {
                if (Array.isArray(value) && (value.includes('Unknown division') || value.includes('Not a valid value'))) {
                  return [key, 'missing_value_selected'];
                }
                return [key, value];
              }));
            }
            this.submitError = errData;
            this.lastSubmitErrorStatus = err.response?.status;
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    transformChildVacancies(data) {
      // Проверяем 2 следущих случая:
      // - есть такая же дочерняя вакансия и она закрыта
      // - есть такая же дочерняя вакансия и она открыта/приостановлена
      let form = { ...data };

      if (!this.childId) {
        form = {
          ...form,
          blocks: [
            {
              ...form.blocks[0],
              parent: this.vacancy.id
            }
          ]
        };
      } else {
        const fetchedChildVacancy = this.getVacancyById(this.childId);
        const fetchedChildVacancyKeys = Object.keys(fetchedChildVacancy);
        fetchedChildVacancyKeys.push('fill_quotas');

        // Если дочерняя вакансия закрыта,
        // то мы обновляем дочернюю вакансию
        // введенными данными в форме, обновляем
        // значение заявки и даты создания/редактирования
        if (fetchedChildVacancy.state === VacancyState.closed) {
          const vacancyCandidate = form.blocks.find((vc) => vc.id === fetchedChildVacancy.id);

          const vacancyCandidateFieldsWithValues = pick(
            vacancyCandidate,
            filter(fetchedChildVacancyKeys, (key) => vacancyCandidate[key])
          );

          form = {
            ...form,
            blocks: [
              {
                ...fetchedChildVacancy,
                ...vacancyCandidateFieldsWithValues,
                state: VacancyState.open,
                created: '',
                updated: '',
                frame_begin: ''
              }
            ]
          };
        } else if (
          fetchedChildVacancy.state === VacancyState.open
          || fetchedChildVacancy.state === VacancyState.hold
        ) {
          const vacancyCandidate = form.blocks.find((vc) => vc.id === fetchedChildVacancy.id);

          const vacancyCandidateFieldsWithValues = pick(
            vacancyCandidate,
            filter(fetchedChildVacancyKeys, (key) => vacancyCandidate[key])
          );

          form = {
            ...form,
            blocks: [
              {
                ...fetchedChildVacancy,
                ...vacancyCandidateFieldsWithValues,
                state: fetchedChildVacancy.state
              }
            ]
          };
        }
      }

      return { form };
    },
    prepareForSubmit(form) {
      // раньше прокатывало без этого, сейчас стало слишком много реактивности 🤔
      form = cloneDeep(form);
      return new Promise((resolve) => {
        if (!this.vacancyRequest) {
          resolve({ form });
          return;
        }

        if (this.multiple || this.childId) {
          resolve(this.transformChildVacancies(form));
        } else {
          resolve({ form });
        }
      });
    }
  }
};
</script>

<style lang="less" module>
.root {
  background-color: #fff;
  border-radius: 16px;
}
</style>
